import axiosInstance from "../Config/axiosConfig";

const CHUNK_SIZE = 2 * 1024 * 1024; 
const MAX_CONCURRENT_UPLOADS = 10;
const MAX_RETRIES = 3;
const RETRY_DELAY = 5000;

export const uploadVideoInChunks = async (
  file,  
  videoId,
  accessToken,
  storedDeviceId,
  storedUserIp,
  onProgress,
  isIntro = false,
  signal = null,
  onStatusUpdate = () => {}
) => {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  let uploadedChunks = 0;
  const chunkQueue = [];
  const failedChunks = new Set();
  const retryQueue = [];
  const successfulChunks = new Set();

  let firstChunk = null;
  let lastChunk = null;

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(file.size, start + CHUNK_SIZE);
    const chunkData = {
      index: i,
      chunk: file.slice(start, end),
      retries: 0,
      isFirst: i === 0,
      isLast: i === totalChunks - 1,
    };

    if (chunkData.isFirst) {
      firstChunk = chunkData;
    } else if (chunkData.isLast) {
      lastChunk = chunkData;
    } else {
      chunkQueue.push(chunkData);
    }
  }

  const updateProgress = () => {
    const percent = ((uploadedChunks / totalChunks) * 100).toFixed(2);
    if (onProgress) onProgress(parseFloat(percent));
  };

  const uploadChunk = async ({ chunk, index, retries, isFirst, isLast }) => {
    if (signal && signal.aborted) {
      return;
    }

    if (successfulChunks.has(index)) {
      uploadedChunks++;
      updateProgress();
      return;
    }

    const formData = new FormData();
    formData.append('fileChunk', chunk);
    formData.append('videoId', videoId);
    formData.append('chunkIndex', index);
    formData.append('totalChunks', totalChunks);
    formData.append('fileSize', file.size);
    formData.append('firstChunk', isFirst);
    formData.append('lastChunk', isLast);

    try {
      const response = await axiosInstance.post(
        '/VideoChunk/upload-video-chunks',
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            UserDevice: storedDeviceId,
            UserIPAddress: storedUserIp,
            'Content-Type': 'multipart/form-data',
          },
          signal,
        }
      );

      if (response.status === 200 && response.data.code === 200) {
        successfulChunks.add(index);
        uploadedChunks++;
        updateProgress();
        onStatusUpdate('uploading');
      } else {
        throw new Error(response.data.message || `Server error: ${response.status}`);
      }

    } catch (err) {
      console.error(`Error uploading chunk ${index}:`, err.message);

      if (axiosInstance.isCancel(err)) {
        console.warn(`Upload cancelled for chunk ${index}`);
        throw err;
      }

      if (retries < MAX_RETRIES) {
        console.warn(`🔁 Retrying chunk ${index} in ${RETRY_DELAY / 1000}s (attempt ${retries + 1})`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return uploadChunk({ chunk, index, retries: retries + 1, isFirst, isLast });
      } else {
        console.error(`🔥 Chunk ${index} failed after ${MAX_RETRIES} retries`);
        failedChunks.add(index);
        retryQueue.push({ chunk, index, retries: 0, isFirst, isLast });
        onStatusUpdate('paused');
      }
    }
  };

  const processChunkQueue = async (queue) => {
    const workers = Array.from({ length: MAX_CONCURRENT_UPLOADS }).map(async () => {
      while (queue.length > 0 && !(signal && signal.aborted)) {
        const chunkItem = queue.shift();
        try {
          await uploadChunk(chunkItem);
        } catch (err) {
          if (!axiosInstance.isCancel(err)) console.error('Worker error:', err.message);
        }
      }
    });
    await Promise.all(workers);
  };

  const retryFailedChunks = async () => {
    if (failedChunks.size === 0) return;
    console.warn(`🔁 Retrying ${failedChunks.size} failed chunks...`);
    const chunksToRetry = Array.from(retryQueue);
    retryQueue.length = 0;
    await processChunkQueue(chunksToRetry);
  };

  try {
    console.log('🚀 Starting upload process...');
    onStatusUpdate('uploading');

    if (firstChunk) await uploadChunk(firstChunk);

    await processChunkQueue(chunkQueue);
    console.log('📦 Finished processing middle chunks');

    if (lastChunk) await uploadChunk(lastChunk);

    if (failedChunks.size > 0) {
      await retryFailedChunks();
    }

    if (signal && signal.aborted) {
      console.warn('🛑 Upload cancelled by user');
      onStatusUpdate('paused');
      return { 
        success: false, 
        status: 'paused',
        message: 'Upload cancelled by user', 
        progress: (uploadedChunks / totalChunks) * 100,
        failedChunks: Array.from(failedChunks)
      };
    }

    if (failedChunks.size > 0) {
      console.warn(`⚠️ Upload completed with ${failedChunks.size} failed chunks`);
      onStatusUpdate('paused');
      return {
        success: false,
        status: 'paused',
        message: `${failedChunks.size} chunks failed to upload. Click upload to retry.`,
        failedChunks: Array.from(failedChunks),
        progress: (uploadedChunks / totalChunks) * 100
      };
    }

    console.log('✅ All chunks uploaded successfully');
    onStatusUpdate('completed');
    return { 
      success: true, 
      status: 'completed',
      message: 'Upload complete!',
      progress: 100
    };

  } catch (error) {
    console.error('❗Upload failed:', error.message);
    onStatusUpdate('paused');
    return {
      success: false,
      status: 'paused',
      message: error.message || 'Upload failed. Please try again.',
      failedChunks: Array.from(failedChunks),
      progress: (uploadedChunks / totalChunks) * 100
    };
  }
};
